import { c as _c } from "react/compiler-runtime";
import type { TimeWeightedVotingConfig } from "./types";
export enum CreateWaveApprovalTimingMode {
  IMMEDIATE = "IMMEDIATE",
  THRESHOLD_TIME = "THRESHOLD_TIME",
  TIME_WEIGHTED = "TIME_WEIGHTED",
}
const APPROVAL_TIMING_OPTIONS: readonly {
  readonly mode: CreateWaveApprovalTimingMode;
  readonly label: string;
  readonly description: string;
}[] = [{
  mode: CreateWaveApprovalTimingMode.IMMEDIATE,
  label: "Immediate",
  description: "Approve as soon as the threshold is reached."
}, {
  mode: CreateWaveApprovalTimingMode.THRESHOLD_TIME,
  label: "Minimum time",
  description: "Require the score to stay above the threshold."
}, {
  mode: CreateWaveApprovalTimingMode.TIME_WEIGHTED,
  label: "Time-weighted",
  description: "Use an averaged vote count over time."
}];
export const getCreateWaveApprovalTimingMode = ({
  thresholdTimeMs,
  timeWeighted
}: {
  readonly thresholdTimeMs: number | null;
  readonly timeWeighted: TimeWeightedVotingConfig;
}): CreateWaveApprovalTimingMode => {
  if (timeWeighted.enabled) {
    return CreateWaveApprovalTimingMode.TIME_WEIGHTED;
  }
  if (thresholdTimeMs !== null && Number.isFinite(thresholdTimeMs)) {
    return CreateWaveApprovalTimingMode.THRESHOLD_TIME;
  }
  return CreateWaveApprovalTimingMode.IMMEDIATE;
};
export default function CreateWaveApprovalTiming(t0) {
  const $ = _c(15);
  const {
    selectedMode,
    errorMessage,
    onModeChange
  } = t0;
  const hasError = errorMessage !== undefined;
  const t1 = hasError ? "approval-timing-error" : undefined;
  const t2 = `tw-mb-3 tw-block tw-text-sm tw-font-semibold ${hasError ? "tw-text-error" : "tw-text-iron-100"}`;
  let t3;
  if ($[0] !== t2) {
    t3 = <legend className={t2}>Approval timing</legend>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onModeChange || $[3] !== selectedMode) {
    t4 = APPROVAL_TIMING_OPTIONS.map(option => {
      const selected = selectedMode === option.mode;
      return <label key={option.mode} className={`tw-flex tw-cursor-pointer tw-gap-x-3 tw-rounded-xl tw-border tw-border-solid tw-p-4 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out ${selected ? "tw-border-primary-400 tw-bg-primary-500/5 tw-ring-primary-500/30" : "tw-border-white/5 tw-bg-iron-900 tw-ring-white/5 hover:tw-border-white/10 hover:tw-bg-iron-800 hover:tw-ring-white/10"}`}><input type="radio" name="approval-timing-mode" value={option.mode} checked={selected} onChange={() => onModeChange(option.mode)} className="tw-mt-1 tw-form-radio tw-h-4 tw-w-4 tw-cursor-pointer tw-border tw-border-solid tw-border-iron-650 tw-bg-iron-800 tw-text-primary-400 tw-ring-offset-iron-800 tw-transition tw-duration-300 tw-ease-out focus:tw-ring-2 focus:tw-ring-primary-400" /><span className="tw-min-w-0"><span className={`tw-block tw-text-sm tw-font-semibold ${selected ? "tw-text-primary-400" : "tw-text-iron-200"}`}>{option.label}</span><span className="tw-mt-1 tw-block tw-text-xs tw-font-medium tw-leading-5 tw-text-iron-400">{option.description}</span></span></label>;
    });
    $[2] = onModeChange;
    $[3] = selectedMode;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-3">{t4}</div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== errorMessage || $[8] !== hasError) {
    t6 = hasError && <p id="approval-timing-error" className="tw-mb-0 tw-mt-2 tw-text-xs tw-font-medium tw-text-error" role="alert">{errorMessage}</p>;
    $[7] = errorMessage;
    $[8] = hasError;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = <fieldset className="tw-mt-6 tw-border-0 tw-p-0" aria-describedby={t1}>{t3}{t5}{t6}</fieldset>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}